(() => {
  // src/lib/utils.ts
  var generateBundleId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

  // src/lib/ensure-stylesheet.ts
  var loaded = /* @__PURE__ */ new Set();
  var ensureStylesheet = (href) => new Promise((resolve) => {
    if (loaded.has(href) || document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
      loaded.add(href);
      resolve();
      return;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => {
      loaded.add(href);
      resolve();
    };
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });
  var hoistAndAwaitStylesheets = async (doc) => {
    const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
    await Promise.all(links.map((l) => ensureStylesheet(l.href)));
    links.forEach((l) => l.remove());
  };

  // src/lib/upsell-debug.ts
  var isUpsellDebug = () => {
    var _a;
    if ((_a = window.HotelCollection) == null ? void 0 : _a.debugRecommendations) return true;
    try {
      return new URLSearchParams(window.location.search).get("rebuy_debug") === "1";
    } catch (e) {
      return false;
    }
  };

  // src/lib/compare-at-price.ts
  var computeCompareAtDiscount = (compareAtCents, priceCents) => {
    if (compareAtCents <= 0 || compareAtCents <= priceCents) return 0;
    return Math.round((compareAtCents - priceCents) / compareAtCents * 100);
  };
  var syncCompareAtInputs = (container, compareAtCents, priceCents) => {
    const capInput = container.querySelector(
      "[data-compare-at-price-input]"
    );
    const discInput = container.querySelector(
      "[data-compare-at-price-discount-input]"
    );
    if (capInput) capInput.value = String(compareAtCents || 0);
    if (discInput)
      discInput.value = String(computeCompareAtDiscount(compareAtCents, priceCents));
  };

  // src/lib/upsell-price.ts
  var formatUpsellMoney = (cents) => {
    var _a;
    const fmt = ((_a = window.Shopify) == null ? void 0 : _a.money_format) || "${{amount}}";
    return fmt.replace("{{amount}}", (cents / 100).toFixed(2)).replace("{{amount_no_decimals}}", String(Math.floor(cents / 100)));
  };
  var upsellPlanPct = (card, planId) => {
    if (!planId) return 0;
    let pct = 0;
    (card.dataset.planDiscounts || "").split(";").forEach((pair) => {
      const [k, v] = pair.split("=");
      if (k === planId) pct = parseInt(v, 10) || 0;
    });
    return pct;
  };
  var syncUpsellPrice = (card) => {
    const priceEl = card.querySelector("[data-upsell-price]");
    if (!priceEl) return;
    const oncePrice = parseInt(card.dataset.oncePrice || "0", 10) || 0;
    const compareAt = parseInt(card.dataset.compareAt || "0", 10) || 0;
    const base = compareAt > 0 ? compareAt : oncePrice;
    const checkbox = card.querySelector("[data-subscribe-checkbox]");
    const planInput = card.querySelector("[data-selling-plan-input]");
    const freqInput = card.querySelector(
      '[data-subscription-frequency] input[name="selling_plan_choice"]'
    );
    const planId = checkbox && checkbox.checked ? freqInput && freqInput.value || planInput && planInput.dataset.sellingPlanId || "" : "";
    const pct = upsellPlanPct(card, planId);
    const price = pct > 0 ? Math.round(base * (100 - pct) / 100) : oncePrice;
    const strike = pct > 0 ? oncePrice > price ? oncePrice : 0 : base > price ? base : 0;
    priceEl.textContent = formatUpsellMoney(price);
    const compareEl = card.querySelector("[data-upsell-compare-price]");
    if (compareEl) {
      compareEl.textContent = strike ? formatUpsellMoney(strike) : "";
      compareEl.hidden = !strike;
    }
    const capInput = card.querySelector("[data-compare-at-price-input]");
    const discInput = card.querySelector("[data-compare-at-price-discount-input]");
    const onceDiscount = parseInt(card.dataset.onceDiscount || "0", 10) || 0;
    if (capInput) capInput.value = String(pct > 0 ? base : compareAt);
    if (discInput) discInput.value = String(pct > 0 ? pct : onceDiscount);
  };

  // src/lib/upsell-swatch.ts
  var syncUpsellSwatch = (swatch) => {
    const card = swatch.closest(".cart-item-upsell");
    if (!card) return;
    const variantId = swatch.dataset.variantId;
    if (variantId) {
      const idInput = card.querySelector('input[name="id"]');
      if (idInput) idInput.value = variantId;
    }
    const compareAt = parseInt(swatch.dataset.variantCompareAtPrice || "0", 10);
    const price = parseInt(swatch.dataset.variantPrice || "0", 10);
    const form = card.querySelector("form");
    if (form) syncCompareAtInputs(form, compareAt, price);
    card.dataset.oncePrice = String(price || 0);
    card.dataset.compareAt = String(compareAt > 0 ? compareAt : price || 0);
    const image = swatch.dataset.image;
    if (image) {
      const img = card.querySelector(".cart-item-upsell__image");
      if (img) img.src = image;
    }
    const variantTitle = swatch.dataset.variantTitle;
    if (variantTitle) {
      const variantEl = card.querySelector(".cart-item-upsell__variant");
      if (variantEl) variantEl.textContent = variantTitle === "Default Title" ? "" : variantTitle;
    }
    card.querySelectorAll("[data-color-swatch]").forEach((s) => s.classList.remove("color-swatch__item--selected"));
    swatch.classList.add("color-swatch__item--selected");
    syncUpsellPrice(card);
  };

  // src/cart-drawer.ts
  (() => {
    var _a;
    const cartEl = document.querySelector(".cart");
    if (!cartEl) return;
    const sectionId = cartEl.dataset.sectionId;
    // Quarterly (90-day) oils carry a 40% discount that no selling plan and no theme code
    // applies — the plans have empty price_adjustments. The discount is driven off the
    // `_quaterly_subs_key` line property (spelling is the app's contract, not a typo to fix)
    // and a forced quantity of 3, exactly as the PDP does in product-purchase-panel.js.
    const QUARTERLY_PLAN_ID = String(
      cartEl.dataset.quarterlySellingPlanId || ""
    ).trim();
    const QUARTERLY_QTY = 3;
    const isQuarterlyPlan = (planId) => !!QUARTERLY_PLAN_ID && String(planId || "") === QUARTERLY_PLAN_ID;
    const cartLog = (...args) => {
      if (isUpsellDebug()) console.log("[cart:upsell]", ...args);
    };
    let loadingCount = 0;
    const setLoading = (on) => {
      loadingCount = Math.max(0, loadingCount + (on ? 1 : -1));
      cartEl.classList.toggle("cart--loading", loadingCount > 0);
    };
    const LOADING_SAFETY_TIMEOUT_MS = 15e3;
    const withLoading = async (fn) => {
      setLoading(true);
      const safetyTimer = window.setTimeout(() => {
        loadingCount = 0;
        cartEl.classList.remove("cart--loading");
      }, LOADING_SAFETY_TIMEOUT_MS);
      try {
        return await fn();
      } finally {
        window.clearTimeout(safetyTimer);
        setLoading(false);
      }
    };
    const cartChange = async (body, signal) => {
      const r = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal
      });
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    };
    const bulkRemove = async (lineKeys) => {
      var _a2, _b;
      const cartState = await fetch("/cart.js").then((r2) => r2.json());
      const keySet = new Set(lineKeys);
      const matchedCount = cartState.items.filter((item) => keySet.has(item.key)).length;
      const updates = cartState.items.map(
        (item) => keySet.has(item.key) ? 0 : item.quantity
      );
      const r = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates })
      });
      if (!r.ok) throw new Error(String(r.status));
      (_b = (_a2 = window.HotelCollection) == null ? void 0 : _a2.RecommendationEngine) == null ? void 0 : _b.clearCache();
      const result = await r.json();
      // Report how many of the requested keys were actually present, so callers can
      // detect a no-op removal (stale/mismatched line key) and reconcile from server
      // truth instead of optimistically dropping a card while the cart is unchanged.
      result._matchedCount = matchedCount;
      return result;
    };
    const findNewItem = (cart, variantId, sellingPlan) => cart.items.find((item) => {
      var _a2, _b;
      if (String(item.variant_id) !== String(variantId)) return false;
      return sellingPlan === null ? !item.selling_plan_allocation : String((_b = (_a2 = item.selling_plan_allocation) == null ? void 0 : _a2.selling_plan) == null ? void 0 : _b.id) === String(sellingPlan);
    });
    const updateCardLineKey = (card, newKey) => {
      card.dataset.lineKey = newKey;
      card.querySelectorAll("[data-line-key]").forEach((el) => {
        el.dataset.lineKey = newKey;
      });
    };
    const findGroupDiffuserCard = (oilCard) => {
      const group = oilCard.closest(".cart-item-container__group");
      return group ? group.querySelector(
        ".cart-item-card[data-linked-oil-variant-id]"
      ) : null;
    };
    const getCardQty = (card) => {
      var _a2;
      return parseInt(
        (_a2 = card.querySelector(
          'input[name="updates[]"], input[name="quantity"]'
        )) == null ? void 0 : _a2.value,
        10
      ) || 1;
    };
    // Switching to the 90-day plan forces quantity 3 (a quarter's worth of oil), matching the
    // PDP. The pre-switch quantity is parked on the card so switching back restores it; a
    // refreshCart in between re-renders the card and drops it, in which case we keep what's
    // on screen rather than snapping the shopper down to 1.
    const resolveQtyForPlan = (card, sellingPlan) => {
      if (isQuarterlyPlan(sellingPlan)) {
        if (!card.dataset.preQuarterlyQty) {
          card.dataset.preQuarterlyQty = String(getCardQty(card));
        }
        return QUARTERLY_QTY;
      }
      const prev = parseInt(card.dataset.preQuarterlyQty || "", 10);
      if (prev > 0) {
        delete card.dataset.preQuarterlyQty;
        return prev;
      }
      return getCardQty(card);
    };
    const syncCardQty = (card, quantity) => {
      const qtyInput = card.querySelector(
        'input[name="updates[]"], input[name="quantity"]'
      );
      if (qtyInput) qtyInput.value = String(quantity);
    };
    // `_compare_at_price_discount` is the NATIVE markdown percentage (compare-at vs price) —
    // never the selling plan's percentage. The discount app reads it as "discount this line
    // already has" and skips any line already at >= 40%, so writing the plan percentage here
    // made the 90-day plan (40) suppress the very "40% OFF QUARTERLY SUBSCRIPTION" discount it
    // was meant to describe: the drawer showed 40% off while checkout charged full price.
    // The subscription discounts do NOT ride on this property — they are driven by the selling
    // plan plus `_quaterly_subs_key`, and apply with no properties present at all.
    // The quarterly key is set only on the 90-day plan. On any other plan it is DELETED from
    // the merged properties in changePlanWithDiscount — not blanked — see the note there.
    const computeDiscountProps = (card, sellingPlan) => {
      const props = {};
      if (isQuarterlyPlan(sellingPlan)) props._quaterly_subs_key = "true";
      const raw = card.dataset.planDiscounts;
      if (!raw) return props;
      const variantPrice = parseInt(card.dataset.variantPrice || "0", 10) || 0;
      const compareAt = parseInt(card.dataset.compareAt || "0", 10) || 0;
      const base = compareAt > 0 ? compareAt : variantPrice;
      props._compare_at_price = String(base);
      props._compare_at_price_discount = String(
        computeCompareAtDiscount(compareAt, variantPrice)
      );
      return props;
    };
    // Change a line's selling plan while keeping its other properties and rewriting the
    // discount property to match the new plan. Returns the updated cart.
    const changePlanWithDiscount = async (lineKey, card, sellingPlan, targetQty) => {
      const discProps = computeDiscountProps(card, sellingPlan);
      let properties;
      if (discProps) {
        const current = await fetch("/cart.js").then((r) => r.json());
        const curItem = current.items.find((i) => i.key === lineKey);
        properties = { ...(curItem == null ? void 0 : curItem.properties || {}), ...discProps };
        // Delete rather than blank. A line added on the 90-day plan carries
        // `_quaterly_subs_key` from the PDP, and these properties are merged forward on every
        // plan change — leaving it (or setting it to "") means a discount app that tests for
        // the key's PRESENCE keeps applying 40% after the shopper switches to 30-day or
        // one-time, which is why those lines showed a discount instead of the full price.
        if (!isQuarterlyPlan(sellingPlan)) {
          delete properties._quaterly_subs_key;
        }
      }
      return cartChange({
        id: lineKey,
        selling_plan: sellingPlan,
        quantity: targetQty,
        ...(properties ? { properties } : {})
      });
    };
    // Mirror of cart-item-card.liquid's subscribed-line display price. The subscription
    // discount is order-level, so Shopify reports the line undiscounted — re-price it from the
    // per-plan percentage in data-plan-discounts. Keep this arithmetic byte-identical to the
    // Liquid side (integer percent, round the UNIT then multiply by quantity).
    const planLinePricing = (card, item) => {
      const raw = card.dataset.planDiscounts;
      const alloc = item.selling_plan_allocation;
      const planId = alloc && alloc.selling_plan ? String(alloc.selling_plan.id) : "";
      if (!raw || !planId) return null;
      let pct = 0;
      raw.split(";").forEach((pair) => {
        const [k, v] = pair.split("=");
        if (k === planId) pct = parseInt(v, 10) || 0;
      });
      if (pct <= 0) return null;
      const variantPrice = parseInt(card.dataset.variantPrice || "0", 10) || 0;
      const compareAt = parseInt(card.dataset.compareAt || "0", 10) || 0;
      const base = compareAt > variantPrice ? compareAt : variantPrice;
      const unit = Math.round(base * (100 - pct) / 100);
      const finalCents = unit * item.quantity;
      const originalCents = base * item.quantity;
      if (originalCents <= finalCents) return null;
      return { finalCents, originalCents };
    };
    // Display price of one cart line: {final, original}. `el` is whatever element carries the
    // line's data-plan-discounts / data-variant-price / data-compare-at — a .cart-item-card for
    // a normal line, a .cart-bundle-card__member <li> for a collapsed bundle component.
    // Mirrors snippets/cart-line-price.liquid; keep the two in step.
    const lineDisplayPricing = (el, item) => {
      const planPricing = planLinePricing(el, item);
      const final = planPricing ? planPricing.finalCents : item.final_line_price;
      const compareAt = item.compare_at_price || parseInt(el.dataset.compareAt) || 0;
      let original = 0;
      if (planPricing) {
        original = planPricing.originalCents;
      } else if (item.selling_plan_allocation) {
        const spa = item.selling_plan_allocation;
        if (spa.compare_at_price > spa.price) {
          original = spa.compare_at_price * item.quantity;
        } else if (item.original_price > spa.price) {
          original = item.original_price * item.quantity;
        } else if (item.final_line_price < item.original_line_price) {
          // Subscription plans carry no price_adjustment; the 30/90 discount is a
          // cart-level automatic discount visible only as final < original line price.
          // Fall through so an in-cart once→subscribe change shows the strikethrough
          // (mirrors cart-item-card.liquid).
          original = item.original_line_price;
        }
      } else if (compareAt > item.original_price) {
        original = compareAt * item.quantity;
      } else if (item.final_line_price < item.original_line_price) {
        original = item.original_line_price;
      }
      return { final, original };
    };
    const formatMoney = (cents) => {
      var _a2;
      const fmt = ((_a2 = window.Shopify) == null ? void 0 : _a2.money_format) || "${{amount}}";
      return fmt.replace("{{amount}}", (cents / 100).toFixed(2)).replace("{{amount_no_decimals}}", String(Math.floor(cents / 100)));
    };
    const upsertStrikethrough = (container, className, finalEl, price) => {
      let sEl = container.querySelector("." + className);
      if (price) {
        if (!sEl) {
          sEl = document.createElement("s");
          sEl.className = className;
          container.insertBefore(sEl, finalEl);
        }
        sEl.textContent = formatMoney(price);
      } else {
        sEl == null ? void 0 : sEl.remove();
      }
    };
    const patchCartPrices = (cart, opts) => {
      const skipLines = !!(opts && opts.skipLines);
      const addonRows = Array.from(
        cartEl.querySelectorAll(".cart-payment-options__add-on-row")
      );
      const addonVariantIdSet = new Set(
        addonRows.map((r) => r.dataset.addonVariantId).filter(Boolean)
      );
      const hasRealItems = cart.items.some(
        (i) => !addonVariantIdSet.has(String(i.variant_id))
      );
      syncAddonEnabled(hasRealItems);
      const writeCardPrice = (card, displayFinal, originalPrice) => {
        const finalEl = card.querySelector(".cart-item-card__price--final");
        if (finalEl) {
          finalEl.textContent = displayFinal === 0 ? "FREE" : formatMoney(displayFinal);
        }
        // Keep the optimistic-scaling baseline in sync for flows that patch from the
        // server (add / remove / plan change), so the next quantity click scales from
        // the right numbers.
        card.dataset.finalCents = String(displayFinal);
        card.dataset.origCents = String(originalPrice > 0 ? originalPrice : displayFinal);
        const priceWrapper = card.querySelector(".cart-item-card__price");
        if (priceWrapper) {
          upsertStrikethrough(
            priceWrapper,
            "cart-item-card__price--original",
            finalEl,
            originalPrice
          );
        }
      };
      if (!skipLines) {
        cart.items.forEach((item) => {
          const card = cartEl.querySelector(
            `.cart-item-card[data-line-key="${item.key}"]`
          );
          // A collapsed bundle card shares its parent's line key but shows the whole group's
          // price, so it is patched in the bundle pass below, not from a single line.
          if (!card || card.classList.contains("cart-bundle-card")) return;
          const { final, original } = lineDisplayPricing(card, item);
          writeCardPrice(card, final, original);
        });
        // Bundle pass: a collapsed card's price is the sum of every line sharing its bundle id.
        const itemsByKey = new Map(cart.items.map((item) => [item.key, item]));
        cartEl.querySelectorAll(".cart-bundle-card").forEach((card) => {
          const keys = (card.dataset.bundleKeys || "").split("|").filter(Boolean);
          if (!keys.length) return;
          let groupFinal = 0;
          let groupOriginal = 0;
          let missing = false;
          keys.forEach((key) => {
            const item = itemsByKey.get(key);
            if (!item) {
              missing = true;
              return;
            }
            // The parent's pricing datasets live on the card root; each component carries its
            // own on its <li>.
            const priceEl = card.querySelector(
              `.cart-bundle-card__member[data-line-key="${key}"]`
            ) || card;
            const { final, original } = lineDisplayPricing(priceEl, item);
            groupFinal += final;
            groupOriginal += original > 0 ? original : final;
          });
          // A member vanished from the cart — the group's composition changed, so let the
          // caller's refresh re-render it rather than showing a half-summed price.
          if (missing) return;
          writeCardPrice(card, groupFinal, groupOriginal);
        });
      }
      // The subtotal has to agree with the lines. Lines display the plan-discounted price while
      // Shopify's cart.total_price does not (order-level discount), so subtract the same
      // per-line delta the cards show. Computed outside the skipLines guard — the subtotal is
      // patched even when the line loop is skipped. Mirrors cart-payment-options.liquid.
      let planDelta = 0;
      let planCompareDelta = 0;
      cart.items.forEach((item) => {
        const card = cartEl.querySelector(
          `.cart-item-card[data-line-key="${item.key}"]`
        );
        if (!card) return;
        const pricing = planLinePricing(card, item);
        if (!pricing) return;
        if (item.final_line_price > pricing.finalCents) {
          planDelta += item.final_line_price - pricing.finalCents;
        }
        if (pricing.originalCents > item.original_line_price) {
          planCompareDelta += pricing.originalCents - item.original_line_price;
        }
      });
      const subtotalPrices = cartEl.querySelector(
        ".cart-payment-options__subtotal-prices"
      );
      if (subtotalPrices) {
        const finalEl = subtotalPrices.querySelector(
          ".cart-payment-options__price--final"
        );
        let displayCents = cart.total_price - planDelta;
        addonRows.forEach((row) => {
          const switchBtn = row.querySelector(".switch");
          if ((switchBtn == null ? void 0 : switchBtn.getAttribute("aria-checked")) !== "true") return;
          const variantId = row.dataset.addonVariantId;
          const inCart = cart.items.some(
            (i) => String(i.variant_id) === String(variantId)
          );
          if (!inCart)
            displayCents += parseInt(row.dataset.addonPriceCents || "0");
        });
        if (finalEl) {
          finalEl.textContent = formatMoney(displayCents);
          finalEl.dataset.totalCents = String(displayCents);
        }
        const compareAtTotal = cart.items.reduce(
          (sum, item) => sum + (item.compare_at_price > item.original_price ? item.compare_at_price * item.quantity : item.original_line_price),
          0
        ) + planCompareDelta;
        const originalTotal = compareAtTotal > displayCents ? compareAtTotal : cart.original_total_price > displayCents ? cart.original_total_price : 0;
        upsertStrikethrough(
          subtotalPrices,
          "cart-payment-options__price--original",
          finalEl,
          originalTotal
        );
      }
      const addonVariantIds = new Set(
        addonRows.map((row) => row.dataset.addonVariantId).filter(Boolean)
      );
      const addonCount = cart.items.filter((i) => addonVariantIds.has(String(i.variant_id))).reduce((sum, i) => sum + i.quantity, 0);
      syncCartCount(Math.max(0, cart.item_count - addonCount));
    };
    const syncAddonEnabled = (hasRealItems) => {
      cartEl.querySelectorAll(
        ".cart-payment-options__add-on-row .switch"
      ).forEach((switchBtn) => {
        if (!hasRealItems) {
          switchBtn.classList.remove("switch--on");
          switchBtn.setAttribute("aria-checked", "false");
          switchBtn.classList.add("switch--disabled");
          switchBtn.disabled = true;
        } else {
          switchBtn.classList.remove("switch--disabled");
          switchBtn.disabled = false;
        }
      });
      const checkoutBtn = cartEl.querySelector(
        ".cart__footer a.button"
      );
      if (checkoutBtn) {
        checkoutBtn.classList.toggle("button--disabled", !hasRealItems);
        checkoutBtn.setAttribute("aria-disabled", String(!hasRealItems));
      }
    };
    const removeStaleAddonsIfNeeded = async (cart) => {
      const addonRows = Array.from(
        cartEl.querySelectorAll(".cart-payment-options__add-on-row")
      );
      const addonVariantIds = new Set(
        addonRows.map((r) => r.dataset.addonVariantId).filter(Boolean)
      );
      const hasRealItems = cart.items.some(
        (i) => !addonVariantIds.has(String(i.variant_id))
      );
      if (hasRealItems) return false;
      const staleKeys = cart.items.filter((i) => addonVariantIds.has(String(i.variant_id))).map((i) => i.key);
      if (staleKeys.length === 0) return false;
      await bulkRemove(staleKeys);
      return true;
    };
    const refreshCart = async () => {
      var _a2, _b;
      (_b = (_a2 = window.HotelCollection) == null ? void 0 : _a2.RecommendationEngine) == null ? void 0 : _b.clearCache();
      try {
        const r = await fetch(`/?sections=${sectionId}`);
        const data = await r.json();
        const html = data[sectionId];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, "text/html");
        await hoistAndAwaitStylesheets(doc);
        const swap = (selector) => {
          const incoming = doc.querySelector(selector);
          const current = cartEl.querySelector(selector);
          if (incoming && current) current.replaceWith(incoming);
        };
        swap(".cart__body");
        swap(".cart__footer");
        const titleEl = cartEl.querySelector(
          ".cart-item-container__title"
        );
        if (titleEl) {
          const count = parseInt(titleEl.dataset.itemCount, 10);
          if (!isNaN(count)) {
            syncCartCount(count);
            syncAddonEnabled(count > 0);
          }
        }
        loadAllUpSells(true);
      } catch (e) {
      }
    };
    const syncCartCount = (count) => {
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = String(count);
      });
      const titleEl = cartEl.querySelector(".cart-item-container__title");
      if (titleEl) {
        titleEl.textContent = `Cart (${count})`;
        titleEl.dataset.itemCount = String(count);
      }
    };
    const IMPRESSION_THRESHOLD = 0.45;
    let impressionObserver = null;
    const SEED_OPTIONAL_INTENTS = /* @__PURE__ */ new Set(["main", "markdown"]);
    const ensureImpressionObserver = () => {
      if (impressionObserver) return impressionObserver;
      impressionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target;
            impressionObserver == null ? void 0 : impressionObserver.unobserve(el);
            const productId = el.dataset.upsellProductId;
            if (!productId) continue;
            document.dispatchEvent(
              new CustomEvent("recommendation:impression", {
                detail: { productId, placementId: "cart-drawer" }
              })
            );
          }
        },
        { threshold: IMPRESSION_THRESHOLD }
      );
      return impressionObserver;
    };
    const upsertHeading = (block, tag, cls, value, before) => {
      if (!value) return;
      let el = block.querySelector(`.${cls}`);
      if (!el) {
        el = document.createElement(tag);
        el.className = cls;
        block.insertBefore(el, before);
      }
      el.textContent = value;
    };
    const renderDebugNote = (block, meta, intent, on) => {
      var _a2, _b, _c, _d, _e;
      const existing = block.querySelector(".cart__upsell-debug");
      if (!on) {
        existing == null ? void 0 : existing.remove();
        return;
      }
      const note = existing != null ? existing : document.createElement("div");
      note.className = "cart__upsell-debug";
      const rows = [
        ["placement", `cart-drawer / ${intent != null ? intent : "\u2014"}`],
        ["type", (_a2 = meta == null ? void 0 : meta.recommendationType) != null ? _a2 : "\u2014"],
        ["datasource", (meta == null ? void 0 : meta.sourceId) || "(default)"],
        ["provider", (_b = meta == null ? void 0 : meta.providerUsed) != null ? _b : "\u2014"],
        ["fallback", (meta == null ? void 0 : meta.fellBack) ? "yes" : "no"],
        ["raw", String((_c = meta == null ? void 0 : meta.rawCount) != null ? _c : "\u2014")],
        ["filtered", String((_d = meta == null ? void 0 : meta.filteredCount) != null ? _d : "\u2014")],
        ["rendered", String((_e = meta == null ? void 0 : meta.renderedCount) != null ? _e : "\u2014")]
      ];
      note.textContent = rows.map(([k, v]) => `${k}: ${v}`).join("  \xB7  ");
      if (!existing) block.insertBefore(note, block.firstChild);
    };
    const MAX_UPSELL_CAROUSELS = 2;
    const loadAllUpSells = async (force = false) => {
      var _a2, _b, _c, _d;
      const productId = [
        ...cartEl.querySelectorAll(
          ".cart-item-card[data-product-id]"
        )
      ].map((el) => el.dataset.productId).filter(Boolean).join(",");
      const seedKey = productId || "__no-seed__";
      if (!force && cartEl.dataset.upsellSeed === seedKey) return;
      cartEl.dataset.upsellSeed = seedKey;
      const engine = (_a2 = window.HotelCollection) == null ? void 0 : _a2.RecommendationEngine;
      const debugOn = isUpsellDebug();
      const blocks = [
        ...cartEl.querySelectorAll("[data-cart-upsell-block]")
      ];
      const entries = await Promise.all(
        blocks.map(async (block) => {
          const intent = block.dataset.intent;
          const sourceId = block.dataset.sourceId || void 0;
          const placeholder = block.querySelector(
            "[data-cart-upsell-block-target]"
          );
          const seedOptional = SEED_OPTIONAL_INTENTS.has(intent != null ? intent : "");
          if (!placeholder || !productId && !seedOptional) {
            return { block, placeholder, intent, result: { html: "" } };
          }
          placeholder.classList.add("is-loading");
          const result = engine ? await engine.fetchHtml({
            productId: productId != null ? productId : "",
            limit: 8,
            intent,
            sourceId
          }) : { html: "" };
          return { block, placeholder, intent, result };
        })
      );
      const nonEmpty = entries.filter((e) => e.result.html);
      const ranked = [
        ...nonEmpty.filter((e) => {
          var _a3;
          return ((_a3 = e.result.meta) == null ? void 0 : _a3.providerUsed) === "rebuy";
        }),
        ...nonEmpty.filter((e) => {
          var _a3;
          return ((_a3 = e.result.meta) == null ? void 0 : _a3.providerUsed) !== "rebuy";
        })
      ];
      const shown = new Set(
        ranked.slice(0, MAX_UPSELL_CAROUSELS).map((e) => e.block)
      );
      for (const { block, placeholder, intent, result } of entries) {
        if (!placeholder) {
          block.setAttribute("hidden", "");
          continue;
        }
        const show = shown.has(block);
        placeholder.innerHTML = show ? result.html : "";
        block.dataset.provider = show ? (_c = (_b = result.meta) == null ? void 0 : _b.providerUsed) != null ? _c : "" : "";
        renderDebugNote(block, result.meta, intent, debugOn);
        block.toggleAttribute("hidden", !show && !debugOn);
        if (show) {
          const headingEl = block.querySelector(
            ".cart__upsell-heading"
          );
          const titleEl = block.querySelector(".cart__upsell-title");
          const titleAnchor = (_d = titleEl != null ? titleEl : headingEl) != null ? _d : placeholder;
          headingEl == null ? void 0 : headingEl.toggleAttribute("hidden", Boolean(result.title || result.superTitle));
          upsertHeading(
            block,
            "h6",
            "cart__upsell-super-title",
            result.superTitle,
            titleAnchor
          );
          upsertHeading(
            block,
            "h4",
            "cart__upsell-title",
            result.title,
            titleAnchor
          );
          upsertHeading(
            block,
            "p",
            "cart__upsell-description",
            result.description,
            placeholder
          );
          const observer = ensureImpressionObserver();
          placeholder.querySelectorAll("[data-upsell-product-id]").forEach((card) => observer.observe(card));
        }
        requestAnimationFrame(() => placeholder.classList.remove("is-loading"));
      }
    };
    const openCart = () => {
      cartEl.removeAttribute("hidden");
      void cartEl.offsetHeight;
      cartEl.classList.add("cart--open");
      document.body.style.overflow = "hidden";
      cartEl.setAttribute("aria-hidden", "false");
      loadAllUpSells();
    };
    const closeCart = () => {
      cartEl.classList.remove("cart--open");
      document.body.style.overflow = "";
      cartEl.setAttribute("aria-hidden", "true");
      impressionObserver == null ? void 0 : impressionObserver.disconnect();
      impressionObserver = null;
      cartEl.addEventListener(
        "transitionend",
        () => cartEl.setAttribute("hidden", ""),
        { once: true }
      );
    };
    const updateLinkedDiffuserPlan = async (diffuserCard, subscribePlanId) => {
      const diffuserLineKey = diffuserCard.dataset.lineKey;
      if (!diffuserLineKey) return;
      try {
        const cart = await cartChange({
          id: diffuserLineKey,
          selling_plan: subscribePlanId,
          quantity: getCardQty(diffuserCard)
        });
        const newItem = findNewItem(
          cart,
          diffuserCard.dataset.variantId,
          subscribePlanId
        );
        if (newItem) updateCardLineKey(diffuserCard, newItem.key);
        patchCartPrices(cart);
      } catch (e) {
        await refreshCart();
      }
    };
    const syncLinkedDiffuser = async (card, variantId, sellingPlan) => {
      if (!card.dataset.linkedDiffuserVariantId) return;
      const diffuserCard = findGroupDiffuserCard(card);
      if (!diffuserCard) return;
      const diffuserPlanId = sellingPlan !== null ? diffuserCard.dataset.subscribePlanId || null : null;
      await updateLinkedDiffuserPlan(diffuserCard, diffuserPlanId);
    };
    const handleRemove = async (removeBtn) => {
      if (cartEl.classList.contains("cart--loading")) return;
      const card = removeBtn.closest(".cart-item-card");
      const lineKey = card == null ? void 0 : card.dataset.lineKey;
      if (!lineKey) return;
      removeBtn.classList.add("cart-item-card__remove--loading");
      card.setAttribute("hidden", "");
      const group = card.closest(".cart-item-container__group");
      const oilCard = group && card.dataset.linkedOilVariantId ? group.querySelector(
        ".cart-item-card[data-linked-diffuser-variant-id]"
      ) : null;
      if (oilCard) oilCard.setAttribute("hidden", "");
      const bundleId2 = card.dataset.bundleId;
      // A bundle is removed as a unit: the trash on a collapsed card takes every line sharing
      // its _bundle_id. Gate on the presence of the bundle id, not on _bundle_role — no writer
      // in the theme sets the "free_diffuser" role, so the old gate never fired.
      const bundleSiblings = bundleId2 ? Array.from(
        cartEl.querySelectorAll(
          `.cart-item-card[data-bundle-id="${bundleId2}"]:not([data-line-key="${lineKey}"])`
        )
      ) : [];
      bundleSiblings.forEach((s) => s.setAttribute("hidden", ""));
      // A collapsed card stands in for lines that have no card of their own, so take the keys
      // straight off the group rather than from sibling DOM nodes.
      const bundleMemberKeys = (card.dataset.bundleKeys || "").split("|").filter(Boolean);
      const linkedDiffuserCard = card.dataset.linkedDiffuserVariantId ? findGroupDiffuserCard(card) : null;
      await withLoading(async () => {
        try {
          const allKeysToRemove = [
            ...new Set([
              lineKey,
              ...(oilCard == null ? void 0 : oilCard.dataset.lineKey) ? [oilCard.dataset.lineKey] : [],
              ...bundleSiblings.map((s) => s.dataset.lineKey).filter(Boolean),
              ...bundleMemberKeys
            ])
          ];
          const cart = await bulkRemove(allKeysToRemove);
          if (linkedDiffuserCard) {
            await updateLinkedDiffuserPlan(linkedDiffuserCard, null);
          }
          if (card.dataset.bundleRole === "free_diffuser_oil" && bundleId2) {
            const remainingOils = cart.items.filter(
              (item) => {
                var _a2, _b, _c;
                return ((_a2 = item.properties) == null ? void 0 : _a2["_bundle_id"]) === bundleId2 && ((_c = (_b = item.properties) == null ? void 0 : _b["_bundle_role"]) == null ? void 0 : _c.includes("oil"));
              }
            );
            if (remainingOils.length === 0) {
              const diffuserItem = cart.items.find(
                (item) => {
                  var _a2, _b;
                  return ((_a2 = item.properties) == null ? void 0 : _a2["_bundle_id"]) === bundleId2 && ((_b = item.properties) == null ? void 0 : _b["_bundle_role"]) === "free_diffuser";
                }
              );
              if (diffuserItem) {
                await cartChange({
                  id: diffuserItem.key,
                  properties: {
                    _bundle_id: "",
                    _bundle_promo: "",
                    _bundle_identifier: ""
                  }
                });
              }
            }
          }
          const cleaned = await removeStaleAddonsIfNeeded(cart);
          const removalNoOp = cart._matchedCount === 0;
          if (cleaned || cart.item_count === 0 || group || removalNoOp) {
            // removalNoOp: the line key wasn't in the cart, so nothing was removed —
            // re-render from server truth so the card doesn't vanish while the
            // subtotal stays put (phantom removal).
            await refreshCart();
          } else {
            card.remove();
            patchCartPrices(cart);
            delete cartEl.dataset.upsellSeed;
            loadAllUpSells();
          }
        } catch (e) {
          removeBtn.classList.remove("cart-item-card__remove--loading");
          card.removeAttribute("hidden");
          oilCard == null ? void 0 : oilCard.removeAttribute("hidden");
          bundleSiblings.forEach((s) => s.removeAttribute("hidden"));
          await refreshCart();
        }
      });
    };
    const handleUpgrade = async (upgradeBtn) => {
      const card = upgradeBtn.closest(".cart-item-card");
      const lineKey = card == null ? void 0 : card.dataset.lineKey;
      const variantId = card == null ? void 0 : card.dataset.variantId;
      const planId = upgradeBtn.dataset.planId;
      if (!lineKey || !planId || !card) return;
      upgradeBtn.classList.add("cart-item-card__upgrade--hiding");
      upgradeBtn.addEventListener(
        "transitionend",
        () => upgradeBtn.setAttribute("hidden", ""),
        { once: true }
      );
      await withLoading(async () => {
        var _a2, _b;
        try {
          const targetQty = resolveQtyForPlan(card, planId);
          const cart = await changePlanWithDiscount(
            lineKey,
            card,
            planId,
            targetQty
          );
          const newItem = findNewItem(cart, variantId, planId);
          if (!newItem || newItem.quantity > targetQty) {
            await refreshCart();
            return;
          }
          updateCardLineKey(card, newItem.key);
          syncCardQty(card, newItem.quantity);
          patchCartPrices(cart);
          await syncLinkedDiffuser(card, variantId, planId);
          const dropdown = card.querySelector(
            ".cart-item-card__plan-dropdown"
          );
          if (dropdown) {
            const matched = dropdown.querySelector(
              `.dropdown__option[data-value="${planId}"]`
            );
            if (matched) {
              dropdown.querySelectorAll(".dropdown__option").forEach((o) => {
                o.classList.toggle("dropdown__option--selected", o === matched);
                o.setAttribute(
                  "aria-selected",
                  o === matched ? "true" : "false"
                );
              });
              const valueEl = dropdown.querySelector(".dropdown__value");
              if (valueEl)
                valueEl.textContent = (_b = (_a2 = matched.textContent) == null ? void 0 : _a2.trim()) != null ? _b : "";
              const hiddenInput = dropdown.querySelector(
                "input[type='hidden']"
              );
              if (hiddenInput) hiddenInput.value = planId;
            }
          }
          upgradeBtn.toggleAttribute("hidden", true);
        } catch (e) {
          await refreshCart();
        }
      });
    };
    const qtyControllers = /* @__PURE__ */ new Map();
    const qtyTimers = /* @__PURE__ */ new Map();
    const flushQty = (lineKey, quantity, lineNumber) => {
      clearTimeout(qtyTimers.get(lineKey));
      qtyTimers.set(
        lineKey,
        setTimeout(async () => {
          var _a2;
          qtyTimers.delete(lineKey);
          (_a2 = qtyControllers.get(lineKey)) == null ? void 0 : _a2.abort();
          const controller = new AbortController();
          qtyControllers.set(lineKey, controller);
          const stepperEl = cartEl.querySelector(
            `.cart-item-card__stepper[data-line-key="${lineKey}"] stepper-component`
          );
          stepperEl == null ? void 0 : stepperEl.classList.add("is-loading");
          await withLoading(async () => {
            try {
              // Target the line by its stable key: after an optimistic card.remove()
              // the remaining cards keep a stale data-line-number, so a `line`-based
              // change would hit the wrong line. The key doesn't shift when other lines
              // are removed.
              // BUT a subscribed line's key can change (subscribe/plan/property rewrite),
              // and if the card's data-line-key is momentarily stale the id-based change
              // 404s — which surfaced as "increment flashes then reverts". So fall back
              // to the positional line on failure, then heal the card's key so later
              // changes use the id path again.
              let cart;
              if (lineKey) {
                try {
                  cart = await cartChange({ id: lineKey, quantity }, controller.signal);
                } catch (idErr) {
                  if (idErr.name === "AbortError") return;
                  if (!lineNumber) throw idErr;
                  cart = await cartChange({ line: lineNumber, quantity }, controller.signal);
                  const healed = cart.items[lineNumber - 1];
                  const healCard = cartEl.querySelector(`.cart-item-card[data-line-key="${lineKey}"]`);
                  if (healed && healCard) updateCardLineKey(healCard, healed.key);
                }
              } else {
                cart = await cartChange({ line: lineNumber, quantity }, controller.signal);
              }
              const cleaned = await removeStaleAddonsIfNeeded(cart);
              if (cleaned) {
                await refreshCart();
              } else {
                // Skip the per-line price rewrite here: a subscribed line's discount
                // allocation (final_line_price) lags one mutation behind server-side,
                // so patching from this response reintroduces the "updates on 2nd click"
                // bug. The line price was already updated optimistically on click by
                // scaling the last-good price by the quantity ratio (see quantity:change
                // handler). The subtotal (cart.total_price) IS fresh, so patch that.
                patchCartPrices(cart, { skipLines: true });
              }
            } catch (err) {
              if (err.name === "AbortError") return;
              await refreshCart();
            } finally {
              stepperEl == null ? void 0 : stepperEl.classList.remove("is-loading");
              if (qtyControllers.get(lineKey) === controller) {
                qtyControllers.delete(lineKey);
              }
            }
          });
        }, 400)
      );
    };
    const handlePlanChange = async (e) => {
      if (!e.target.classList.contains(
        "cart-item-card__plan-dropdown"
      ))
        return;
      const card = e.target.closest(
        ".cart-item-card"
      );
      const lineKey = card == null ? void 0 : card.dataset.lineKey;
      const variantId = card == null ? void 0 : card.dataset.variantId;
      const sellingPlan = e.detail.value === "purchase_once" ? null : e.detail.value;
      if (!lineKey || !card) return;
      // A quarterly line renders a LOCKED stepper reading 1, where every other line renders an
      // editable stepper showing the real quantity (cart-item-card.liquid). This handler
      // patches the card in place rather than re-rendering it, so crossing into or out of the
      // quarterly plan would leave the wrong control on screen — read the current state now
      // and re-render below when it no longer matches.
      const wasQuarterlyCard = card.dataset.quarterlyLine === "true";
      const dropdownEl = e.target.closest(".dropdown");
      dropdownEl == null ? void 0 : dropdownEl.classList.add("is-loading");
      await withLoading(async () => {
        try {
          const targetQty = resolveQtyForPlan(card, sellingPlan);
          const cart = await changePlanWithDiscount(
            lineKey,
            card,
            sellingPlan,
            targetQty
          );
          const newItem = findNewItem(cart, variantId, sellingPlan);
          if (!newItem) {
            await refreshCart();
            return;
          }
          updateCardLineKey(card, newItem.key);
          syncCardQty(card, newItem.quantity);
          patchCartPrices(cart);
          await syncLinkedDiffuser(card, variantId, sellingPlan);
          const upgradeBtn = card.querySelector("[data-upgrade-btn]");
          if (upgradeBtn) {
            upgradeBtn.toggleAttribute("hidden", sellingPlan !== null);
          }
          // Locked <-> editable stepper swap: only Liquid can rebuild either control, so fall
          // back to a section re-render when the plan crossed the quarterly boundary. Runs
          // last so the in-place patches above still apply if the refresh fails.
          if (wasQuarterlyCard !== isQuarterlyPlan(sellingPlan)) {
            await refreshCart();
          }
        } catch (e2) {
          await refreshCart();
        } finally {
          dropdownEl == null ? void 0 : dropdownEl.classList.remove("is-loading");
        }
      });
    };
    const handleFormSubmit = async (e) => {
      var _a2, _b, _c;
      const form = e.target.closest(
        'form[action="/cart/add"], form[action*="/cart/add"]'
      );
      if (!form) return;
      if (form.closest("[data-buy-bar]")) return;
      e.preventDefault();
      const isInCart = cartEl.contains(form);
      if (isInCart && cartEl.classList.contains("cart--loading")) return;
      const upsellBlock = form.closest(".cart-item-upsell") && form.closest("[data-cart-upsell-block]");
      if (upsellBlock) {
        cartLog("upsell add intercepted", {
          variantId: ((_a2 = form.querySelector('input[name="id"]')) == null ? void 0 : _a2.value) || "",
          intent: upsellBlock.dataset.intent,
          sourceId: upsellBlock.dataset.sourceId || "(default)",
          provider: upsellBlock.dataset.provider
        });
      }
      const oilVariantId = ((_b = form.querySelector("[data-oil-variant-id]")) == null ? void 0 : _b.value) || "";
      const qty = Math.max(
        1,
        parseInt(
          (_c = form.querySelector('input[name="quantity"]')) == null ? void 0 : _c.value,
          10
        ) || 1
      );
      const oilSelector = form.querySelector("[data-include-oil-selector]");
      const oilIncluded = !oilSelector || !oilSelector.hidden;
      const doAdd = async () => {
        var _a3;
        try {
          const diffuserData = new FormData(form);
          // Upsell card subscribed on the 90-day plan — same quarterly contract as the PDP.
          if (isQuarterlyPlan(diffuserData.get("selling_plan"))) {
            diffuserData.append("properties[_quaterly_subs_key]", "true");
            diffuserData.set("quantity", String(QUARTERLY_QTY));
          }
          if (upsellBlock) {
            const source = upsellBlock.dataset.provider === "rebuy" ? "Rebuy" : "Shopify";
            diffuserData.append("properties[_source]", source);
            diffuserData.append(
              "properties[_attribution]",
              JSON.stringify({
                intent: upsellBlock.dataset.intent || "",
                sourceId: upsellBlock.dataset.sourceId || "",
                placement: upsellBlock.dataset.placement || "cart-drawer"
              })
            );
            cartLog("attribution stamped", {
              source,
              intent: upsellBlock.dataset.intent
            });
          }
          let oilBody = null;
          if (oilVariantId && oilIncluded) {
            const bundleId2 = generateBundleId();
            diffuserData.append("properties[_bundle_id]", bundleId2);
            diffuserData.append("properties[_bundle_promo]", "free-diffuser-set");
            diffuserData.append("properties[_bundle_identifier]", "true");
            diffuserData.append("properties[_bundle_role]", "diffuser");
            diffuserData.append("properties[_oil_variant_id]", oilVariantId);
            const oilSellingPlanId = ((_a3 = form.querySelector("[data-oil-selling-plan-id]")) == null ? void 0 : _a3.value) || "";
            const isSubscribe = !!form.querySelector(
              '[data-toggle="subscribe"][aria-pressed="true"]'
            );
            const diffuserVariantId = diffuserData.get("id") || "";
            const oilIsQuarterly = isSubscribe && isQuarterlyPlan(oilSellingPlanId);
            oilBody = new FormData();
            oilBody.append("id", oilVariantId);
            oilBody.append("quantity", String(oilIsQuarterly ? QUARTERLY_QTY : qty));
            oilBody.append("properties[_bundle_id]", bundleId2);
            oilBody.append("properties[_bundle_promo]", "free-diffuser-set");
            oilBody.append("properties[_bundle_identifier]", "true");
            if (oilIsQuarterly)
              oilBody.append("properties[_quaterly_subs_key]", "true");
            if (isSubscribe && oilSellingPlanId)
              oilBody.append("selling_plan", oilSellingPlanId);
            if (diffuserVariantId)
              oilBody.append(
                "properties[_linked_diffuser_variant_id]",
                String(diffuserVariantId)
              );
          }
          if (upsellBlock)
            cartLog("\u2192 POST /cart/add.js", { id: diffuserData.get("id") });
          const diffuserRes = await fetch("/cart/add.js", {
            method: "POST",
            body: diffuserData
          });
          if (!diffuserRes.ok) throw new Error("cart/add failed");
          await diffuserRes.json();
          if (upsellBlock)
            cartLog("\u2713 added to cart", { id: diffuserData.get("id") });
          if (oilBody) {
            const oilRes = await fetch("/cart/add.js", {
              method: "POST",
              body: oilBody
            });
            if (!oilRes.ok) throw new Error("cart/add oil failed");
            await oilRes.json();
          }
          await refreshCart();
          openCart();
          document.dispatchEvent(
            new CustomEvent("cart:item-added", {
              detail: {
                variantId: String(diffuserData.get("id") || ""),
                isUpsell: !!upsellBlock
              }
            })
          );
        } catch (err) {
          if (upsellBlock)
            cartLog("\u2717 add failed \u2014 falling back to native submit", err);
          form.submit();
        }
      };
      if (isInCart) {
        await withLoading(doAdd);
      } else {
        await doAdd();
      }
    };
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-cart-open]")) openCart();
      if (e.target.closest("[data-cart-close]")) closeCart();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && cartEl.classList.contains("cart--open"))
        closeCart();
    });
    // Update the line price the instant a stepper is clicked, by scaling the last-good
    // price by the quantity ratio. This is discount-agnostic (works for subscribed oils
    // whose server-side discount allocation lags a mutation behind) and linear-exact for
    // undiscounted lines. The debounced server sync deliberately skips the per-line price
    // (patchCartPrices skipLines) so it can't clobber this with a stale value.
    const updateLinePriceOptimistic = (lineKey, quantity, previousQuantity) => {
      if (!previousQuantity || previousQuantity <= 0 || !quantity) return;
      const card = cartEl.querySelector(`.cart-item-card[data-line-key="${lineKey}"]`);
      if (!card) return;
      const finalEl = card.querySelector(".cart-item-card__price--final");
      const prevFinal = parseInt(card.dataset.finalCents || "", 10);
      if (!finalEl || isNaN(prevFinal)) return;
      const newFinal = Math.round(prevFinal * quantity / previousQuantity);
      card.dataset.finalCents = String(newFinal);
      finalEl.textContent = newFinal === 0 ? "FREE" : formatMoney(newFinal);
      const prevOrig = parseInt(card.dataset.origCents || "", 10);
      const priceWrapper = card.querySelector(".cart-item-card__price");
      if (priceWrapper && !isNaN(prevOrig)) {
        const newOrig = Math.round(prevOrig * quantity / previousQuantity);
        card.dataset.origCents = String(newOrig);
        upsertStrikethrough(
          priceWrapper,
          "cart-item-card__price--original",
          finalEl,
          newOrig > newFinal ? newOrig : 0
        );
      }
    };
    // A collapsed bundle's stepper counts BUNDLES, not lines: one click has to rescale every
    // member line at its own per-bundle ratio. Do it as a single /cart/update.js so the group
    // can never land half-updated, then re-render — the component "x N" labels and the stepper
    // value are Liquid-side, and only a re-render can rebuild them.
    const bundleQtyTimers = /* @__PURE__ */ new Map();
    const flushBundleQty = (card, bundleUnits) => {
      const bundleId = card.dataset.bundleId;
      if (!bundleId || !bundleUnits || bundleUnits < 1) return;
      clearTimeout(bundleQtyTimers.get(bundleId));
      bundleQtyTimers.set(
        bundleId,
        setTimeout(async () => {
          var _a2, _b;
          bundleQtyTimers.delete(bundleId);
          const stepperEl = card.querySelector(
            ".cart-item-card__stepper stepper-component"
          );
          stepperEl == null ? void 0 : stepperEl.classList.add("is-loading");
          await withLoading(async () => {
            try {
              const unitsByKey = /* @__PURE__ */ new Map();
              unitsByKey.set(
                card.dataset.lineKey,
                parseInt(card.dataset.parentUnits || "1", 10) || 1
              );
              card.querySelectorAll(".cart-bundle-card__member").forEach((li) => {
                if (!li.dataset.lineKey) return;
                unitsByKey.set(
                  li.dataset.lineKey,
                  parseInt(li.dataset.unitsPerBundle || "1", 10) || 1
                );
              });
              const cartState = await fetch("/cart.js").then((r2) => r2.json());
              const updates = cartState.items.map(
                (item) => unitsByKey.has(item.key) ? unitsByKey.get(item.key) * bundleUnits : item.quantity
              );
              const r = await fetch("/cart/update.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ updates })
              });
              if (!r.ok) {
                // Purchase limits are enforced per variant, so a bundle can be blocked by one
                // member hitting its cap. Surface it — the refresh below silently snaps the
                // stepper back and would otherwise look like a dead button.
                const detail = await r.json().catch(() => null);
                throw new Error(
                  (detail && (detail.description || detail.message)) || String(r.status)
                );
              }
              (_b = (_a2 = window.HotelCollection) == null ? void 0 : _a2.RecommendationEngine) == null ? void 0 : _b.clearCache();
            } catch (err) {
              console.warn("[cart] bundle quantity update failed:", err && err.message);
            } finally {
              stepperEl == null ? void 0 : stepperEl.classList.remove("is-loading");
              await refreshCart();
            }
          });
        }, 400)
      );
    };
    cartEl.addEventListener("quantity:change", (e) => {
      const { lineKey, lineNumber, quantity, previousQuantity } = e.detail;
      const bundleCard = cartEl.querySelector(
        `.cart-bundle-card[data-line-key="${lineKey}"]`
      );
      if (bundleCard) {
        // No optimistic price scaling here: a bundle mixes discounted and undiscounted lines,
        // and scaling a discounted line by the quantity ratio removes the discount instead of
        // scaling it. Show the loading state and take the server's numbers.
        flushBundleQty(bundleCard, quantity);
        return;
      }
      updateLinePriceOptimistic(lineKey, quantity, previousQuantity);
      flushQty(lineKey, quantity, lineNumber);
    });
    cartEl.addEventListener("click", async (e) => {
      const swatch = e.target.closest(
        ".cart-item-upsell [data-color-swatch]"
      );
      if (swatch) {
        syncUpsellSwatch(swatch);
        return;
      }
      const removeBtn = e.target.closest(".cart-item-card__remove");
      if (removeBtn) {
        await handleRemove(removeBtn);
        return;
      }
      const upgradeBtn = e.target.closest("[data-upgrade-btn]");
      if (upgradeBtn) {
        await handleUpgrade(upgradeBtn);
        return;
      }
      const checkoutBtn = e.target.closest(
        ".cart__footer a.button"
      );
      if (checkoutBtn) await handleCheckout(checkoutBtn, e);
    });
    cartEl.addEventListener("dropdown:change", handlePlanChange);
    document.addEventListener("submit", handleFormSubmit);
    document.addEventListener("change", (e) => {
      var _a2;
      const checkbox = e.target.closest(
        "[data-subscribe-checkbox]"
      );
      if (!checkbox) return;
      const card = checkbox.closest(".cart-item-upsell");
      if (!card) return;
      const planInput = card.querySelector(
        "[data-selling-plan-input]"
      );
      const freq = card.querySelector(
        "[data-subscription-frequency]"
      );
      if (!planInput) return;
      if (checkbox.checked) {
        if (freq) freq.hidden = false;
        const chosen = (_a2 = freq == null ? void 0 : freq.querySelector(
          'input[name="selling_plan_choice"]'
        )) == null ? void 0 : _a2.value;
        planInput.value = chosen || planInput.dataset.sellingPlanId || "";
      } else {
        if (freq) freq.hidden = true;
        planInput.value = "";
      }
      syncUpsellPrice(card);
    });
    document.addEventListener("dropdown:change", (e) => {
      var _a2;
      const card = e.target.closest(
        ".cart-item-upsell"
      );
      if (!card) return;
      if (!e.target.closest("[data-subscription-frequency]"))
        return;
      const planInput = card.querySelector(
        "[data-selling-plan-input]"
      );
      if (!planInput) return;
      const id = ((_a2 = e.detail) == null ? void 0 : _a2.value) || "";
      planInput.dataset.sellingPlanId = id;
      const checkbox = card.querySelector(
        "[data-subscribe-checkbox]"
      );
      if (checkbox == null ? void 0 : checkbox.checked) planInput.value = id;
      syncUpsellPrice(card);
    });
    cartEl.addEventListener("click", (e) => {
      const switchBtn = e.target.closest(".switch");
      if (!switchBtn) return;
      const row = switchBtn.closest(
        ".cart-payment-options__add-on-row"
      );
      if (!row) return;
      const priceCents = parseInt(row.dataset.addonPriceCents || "0");
      const turningOn = switchBtn.getAttribute("aria-checked") !== "true";
      const finalEl = cartEl.querySelector(
        ".cart-payment-options__price--final"
      );
      if (finalEl && priceCents) {
        const currentCents = parseInt(finalEl.dataset.totalCents || "0");
        const newCents = currentCents + (turningOn ? priceCents : -priceCents);
        finalEl.textContent = formatMoney(newCents);
        finalEl.dataset.totalCents = String(newCents);
      }
    });
    const handleCheckout = async (btn, e) => {
      if (btn.getAttribute("aria-disabled") === "true") {
        e.preventDefault();
        return;
      }
      const href = btn.getAttribute("href") || "/checkout";
      const addonRows = Array.from(
        cartEl.querySelectorAll(".cart-payment-options__add-on-row")
      );
      if (addonRows.length === 0) return;
      e.preventDefault();
      setLoading(true);
      try {
        const cart = await fetch("/cart.js").then((r) => r.json());
        for (const row of addonRows) {
          const variantId = row.dataset.addonVariantId;
          const item = cart.items.find(
            (i) => String(i.variant_id) === String(variantId)
          );
          if (item) await cartChange({ id: item.key, quantity: 0 });
        }
        for (const row of addonRows) {
          const switchBtn = row.querySelector(".switch");
          if ((switchBtn == null ? void 0 : switchBtn.getAttribute("aria-checked")) !== "true") continue;
          const variantId = row.dataset.addonVariantId;
          if (!variantId) continue;
          const compareAt = parseInt(row.dataset.addonCompareAtPrice || "0", 10);
          const priceCents = parseInt(row.dataset.addonPriceCents || "0", 10);
          const r = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              properties: {
                _compare_at_price: String(compareAt),
                _compare_at_price_discount: String(
                  computeCompareAtDiscount(compareAt, priceCents)
                )
              }
            })
          });
          if (!r.ok) throw new Error(String(r.status));
        }
        window.location.href = href;
      } catch (e2) {
        setLoading(false);
      }
    };
    let openAddonPopup = null;
    const closeAddonPopup = () => {
      if (openAddonPopup) {
        openAddonPopup.hidden = true;
        openAddonPopup = null;
      }
    };
    cartEl.addEventListener("click", (e) => {
      var _a2;
      const btn = e.target.closest(
        "[data-addon-info-btn]"
      );
      if (btn) {
        e.stopPropagation();
        const popup = (_a2 = btn.closest(".cart-payment-options__add-on-row")) == null ? void 0 : _a2.querySelector(".cart-add-on-popup");
        if (!popup) return;
        if (popup === openAddonPopup) {
          closeAddonPopup();
          return;
        }
        closeAddonPopup();
        popup.hidden = false;
        openAddonPopup = popup;
        return;
      }
      if (openAddonPopup && !e.target.closest(".cart-add-on-popup")) {
        closeAddonPopup();
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeAddonPopup();
    });
    const initTitleEl = cartEl.querySelector(
      ".cart-item-container__title"
    );
    const initCount = parseInt((_a = initTitleEl == null ? void 0 : initTitleEl.dataset.itemCount) != null ? _a : "", 10);
    if (!isNaN(initCount)) {
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = String(initCount);
      });
      syncAddonEnabled(initCount > 0);
    }
    window.HotelCollection = window.HotelCollection || {};
    window.HotelCollection.openCart = openCart;
    window.HotelCollection.refreshCart = refreshCart;
  })();
})();
